/**
 * What to ask of the subject's voice while a sample records (ADR-0062), from the microphone's
 * true level: *closer to the phone or louder* when the voice is too faint, *further from the
 * phone* when the sound clips. Either is said only once it has lasted about two seconds — a
 * mumbled word or one loud laugh asks for nothing — and goes when it is fine again.
 *
 * Too faint: a sound stands over the room's noise, so somebody is speaking, and for two seconds
 * of it no frame has reached the level a voice should (`VOICE_FAINT_BELOW_DBFS`). One frame that
 * does reach it ends that at once: the subject came closer, and the caption says so without a
 * lag. A silence is no faint voice: after a moment of it the guidance goes, and the caption waits
 * for a voice again.
 *
 * Clipping comes in bursts, on the loud syllables, so it is counted as a stretch: clipped frames
 * no further apart than a second. A stretch of two seconds is said, and stays said until no frame
 * has clipped for a moment. Clipping is said before faintness: it damages the sample more.
 *
 * It hears loudness, not words: a noise that starts mid-sample and stays under the level of a
 * voice reads as a faint one. The thresholds are in `voice-rules.ts`, all provisional. Pure.
 */
import {
  VOICE_ASSUMED_ROOM_DBFS,
  VOICE_CLIPPING_GAP_MS,
  VOICE_FAINT_BELOW_DBFS,
  VOICE_GUIDANCE_AFTER_MS,
  VOICE_GUIDANCE_CLEAR_MS,
  VOICE_GUIDANCE_REPEAT_MS,
  VOICE_HEARD_OVER_ROOM_DB,
} from '@/components/humint/voice/voice-rules'
import type { VoiceAnnounced, VoiceGuidance } from '@/components/humint/voice/voice-types'

/** One frame of the microphone's true level, and the time since the one before it. */
export interface GuidanceSample {
  /** dB under full scale. */
  dbfs: number
  clipping: boolean
  dtMs: number
}

/** The guidance between two frames. */
export interface GuidanceTrack {
  /** A sound over the room that never reached a voice's level: for how long, since the last frame that did. */
  faintMs: number
  /** For how long nothing has been heard over the room. */
  silentMs: number
  /** From the first clipped frame of the stretch under way to its last; 0 for none, or for one frame. */
  clippingMs: number
  /** Since the last clipped frame; null before the first. */
  sinceClipMs: number | null
  /** What the caption asks now. */
  shown: VoiceGuidance | null
}

export const NO_GUIDANCE: GuidanceTrack = {
  faintMs: 0,
  silentMs: 0,
  clippingMs: 0,
  sinceClipMs: null,
  shown: null,
}

type Clipping = Pick<GuidanceTrack, 'clippingMs' | 'sinceClipMs'>

/**
 * The stretch of clipping after one more frame. A clipped frame no further than the gap from the
 * one before it lengthens the stretch to itself; one that comes later starts a new stretch. The
 * frames between two clipped ones add nothing by themselves: two lone peaks are no stretch.
 */
function trackClipping(track: GuidanceTrack, { clipping, dtMs }: GuidanceSample): Clipping {
  const since = track.sinceClipMs === null ? null : track.sinceClipMs + dtMs
  const joins = since !== null && since <= VOICE_CLIPPING_GAP_MS
  if (clipping) return { clippingMs: joins ? track.clippingMs + since : 0, sinceClipMs: 0 }
  return { clippingMs: joins ? track.clippingMs : 0, sinceClipMs: since }
}

type Faintness = Pick<GuidanceTrack, 'faintMs' | 'silentMs'>

/**
 * The faint voice after one more frame. `roomDbfs`: the room's noise as the check measured it. A
 * frame at a voice's level ends it; a frame over the room and under that level adds to it; a
 * frame of the room alone adds nothing, and a moment of those ends it too.
 */
function trackFaintness(track: GuidanceTrack, sample: GuidanceSample, roomDbfs: number): Faintness {
  const { dbfs, dtMs } = sample
  if (dbfs >= VOICE_FAINT_BELOW_DBFS) return { faintMs: 0, silentMs: 0 }
  if (dbfs >= roomDbfs + VOICE_HEARD_OVER_ROOM_DB) {
    return { faintMs: track.faintMs + dtMs, silentMs: 0 }
  }
  const silentMs = track.silentMs + dtMs
  return { faintMs: silentMs >= VOICE_GUIDANCE_CLEAR_MS ? 0 : track.faintMs, silentMs }
}

/** What the caption asks, from the two measures and what it asked before. */
function shownOf(
  previous: VoiceGuidance | null,
  clip: Clipping,
  faint: Faintness
): VoiceGuidance | null {
  const clippedOn = clip.clippingMs >= VOICE_GUIDANCE_AFTER_MS
  // Once said, clipping stays said until no frame has clipped for a moment.
  const clippedStill =
    previous === 'clipping' &&
    clip.sinceClipMs !== null &&
    clip.sinceClipMs < VOICE_GUIDANCE_CLEAR_MS
  if (clippedOn || clippedStill) return 'clipping'
  return faint.faintMs >= VOICE_GUIDANCE_AFTER_MS ? 'faint' : null
}

/** The guidance after one more frame. `roomDbfs` null: the room check could not tell the room. */
export function advanceGuidance(
  track: GuidanceTrack,
  sample: GuidanceSample,
  roomDbfs: number | null
): GuidanceTrack {
  if (sample.dtMs <= 0) return track
  const clip = trackClipping(track, sample)
  const faint = trackFaintness(track, sample, roomDbfs ?? VOICE_ASSUMED_ROOM_DBFS)
  return { ...clip, ...faint, shown: shownOf(track.shown, clip, faint) }
}

/**
 * For how long nobody has been heard, as a screen reader is told it: the shorter of what the
 * speech meter says and of what the true level says. A voice too faint to count is still a
 * voice: while one is heard it is not *waiting for a voice* that is said, but — once it has
 * lasted — what to ask of it.
 */
export const silenceToTell = (countedSilentMs: number, track: GuidanceTrack): number =>
  Math.min(countedSilentMs, track.silentMs)

/** The guidance a screen reader was last told, and when it last stood told. */
export interface GuidanceTold {
  guidance: VoiceGuidance
  atMs: number
}

/**
 * Whether a screen reader is told the guidance the caption shows. One that it is being told
 * stays told for as long as it is shown. One that appears is told — unless the same was told
 * less than `VOICE_GUIDANCE_REPEAT_MS` ago: a voice at the edge of the threshold would otherwise
 * have it said every few seconds, over the subject.
 */
export function tellsGuidance(
  shown: VoiceGuidance | null,
  previous: VoiceAnnounced | null,
  told: GuidanceTold | null,
  nowMs: number
): boolean {
  if (!shown) return false
  if (previous === shown) return true
  return !told || told.guidance !== shown || nowMs - told.atMs >= VOICE_GUIDANCE_REPEAT_MS
}
